import { Component, OnInit } from '@angular/core';
import { take, catchError } from 'rxjs/operators';
import { ReportBuilderService } from '../../services/report-builder.service';
import { SavedReportService } from '../../services/saved-report.service';
import { ReportCsvService } from 'src/app/warehousing/services/report-csv.service';
import { ReportColumn, ReportEntitySchema } from '../../models/report-builder-schema.model';
import {
  REPORT_FILTER_OPERATORS, ReportBuilderAggregateSpec, ReportBuilderFilter, ReportResult, ReportSpec,
} from '../../models/report-builder-spec.model';
import { GridColumn } from 'src/app/core/models/grid-column.model';
import { SavedReport } from '../../models/saved-report.model';

/** Everything needed to rebuild this page's picker state on reload - not the narrower ReportSpec. */
interface AggregateBuilderState {
  joins: string[];
  isAggregate: boolean;
  columns: string[];
  groupBy: string[];
  aggregates: ReportBuilderAggregateSpec[];
  filters: ReportBuilderFilter[];
  sort: string;
  direction: string;
  limit: number;
}

/**
 * The one screen `<app-grid>` cannot cover: GROUP BY / SUM / COUNT / AVG / MIN / MAX, with the same
 * curated join whitelist as Basic mode but resolved server-side by `ReportBuilderEngine` rather
 * than a CRUD endpoint. Columns aren't known until a run completes, so the result table is built
 * from the response rather than declared up front - same `GridColumn` model the rest of the app uses
 * for dynamic columns.
 */
@Component({
  selector: 'app-report-builder-aggregate',
  templateUrl: './report-builder-aggregate.component.html',
  styleUrls: ['./report-builder-aggregate.component.scss'],
  standalone: false
})
export class ReportBuilderAggregateComponent implements OnInit {

  schemas: ReportEntitySchema[] = [];
  loadFailed: boolean = false;

  selectedEntityKey: string = '';
  schema: ReportEntitySchema = null;

  selectedJoins: Record<string, boolean> = {};
  availableColumns: ReportColumn[] = [];

  isAggregate: boolean = false;

  selectedColumns: Record<string, boolean> = {};
  selectedGroupBy: Record<string, boolean> = {};
  aggregateRows: ReportBuilderAggregateSpec[] = [];

  filters: ReportBuilderFilter[] = [];
  operators = REPORT_FILTER_OPERATORS;

  sort: string = '';
  direction: string = 'asc';
  limit: number = 500;

  running: boolean = false;
  hasRun: boolean = false;
  runFailed: boolean = false;
  runError: string = '';

  result: ReportResult = new ReportResult();
  resultColumns: GridColumn[] = [];

  savedReports: SavedReport[] = [];
  selectedSavedReportId: number = -1;
  currentSavedReport: SavedReport = null;
  reportName: string = '';
  saving: boolean = false;
  saveFailed: boolean = false;

  constructor(
    private reportBuilderService: ReportBuilderService,
    private savedReportService: SavedReportService,
    private reportCsvService: ReportCsvService,
  ) { }

  ngOnInit(): void {
    this.reportBuilderService.getSchema()
      .pipe(take(1))
      .pipe(catchError((e) => { this.loadFailed = true; return e; }))
      .subscribe((schemas: ReportEntitySchema[]) => { this.schemas = schemas || []; });
  }

  onEntityChanged(key: string): void {
    this.selectedEntityKey = key;
    this.schema = this.schemas.find((s) => s.key === key) || null;

    this.selectedJoins = {};
    this.selectedColumns = {};
    this.selectedGroupBy = {};
    this.aggregateRows = [];
    this.filters = [];
    this.sort = '';
    this.hasRun = false;

    this.savedReports = [];
    this.selectedSavedReportId = -1;
    this.currentSavedReport = null;
    this.reportName = '';

    this.recomputeAvailableColumns();

    if (this.schema) {
      this.savedReportService.getAllByEntity(this.schema.key)
        .pipe(take(1))
        .pipe(catchError((e) => e))
        .subscribe((reports: SavedReport[]) => {
          this.savedReports = (reports || []).filter((r) => r.mode === 'aggregate');
        });
    }
  }

  toggleJoin(key: string): void {
    this.selectedJoins[key] = !this.selectedJoins[key];
    this.recomputeAvailableColumns();
  }

  private recomputeAvailableColumns(): void {
    if (!this.schema) {
      this.availableColumns = [];
      return;
    }

    let columns: ReportColumn[] = [...this.schema.columns];

    this.schema.joins
      .filter((j) => this.selectedJoins[j.key])
      .forEach((j) => { columns = columns.concat(j.columns); });

    this.availableColumns = columns;

    // Filters/columns/groupBy may reference a column from a join that has just been unticked -
    // drop anything no longer resolvable rather than send the backend a spec it will 400 on.
    const validKeys = columns.map((c) => c.key);
    this.filters = this.filters.filter((f) => validKeys.indexOf(f.column) !== -1);
    Object.keys(this.selectedColumns).forEach((k) => { if (validKeys.indexOf(k) === -1) delete this.selectedColumns[k]; });
    Object.keys(this.selectedGroupBy).forEach((k) => { if (validKeys.indexOf(k) === -1) delete this.selectedGroupBy[k]; });
  }

  toggleColumn(key: string): void {
    this.selectedColumns[key] = !this.selectedColumns[key];
  }

  toggleGroupBy(key: string): void {
    this.selectedGroupBy[key] = !this.selectedGroupBy[key];
  }

  groupableColumns(): ReportColumn[] {
    return this.availableColumns.filter((c) => c.groupable);
  }

  addAggregateRow(): void {
    const first = this.schema && this.schema.aggregates.length > 0 ? this.schema.aggregates[0] : null;
    const row = new ReportBuilderAggregateSpec();

    if (first) {
      row.column = first.key;
      row.fn = first.functions[0];
    }

    this.aggregateRows.push(row);
  }

  removeAggregateRow(index: number): void {
    this.aggregateRows.splice(index, 1);
  }

  functionsFor(columnKey: string): string[] {
    const column = this.schema ? this.schema.aggregates.find((a) => a.key === columnKey) : null;
    return column ? column.functions : [];
  }

  addFilter(): void {
    const filter = new ReportBuilderFilter();

    if (this.availableColumns.length > 0) {
      filter.column = this.availableColumns[0].key;
      filter.operator = this.operatorsFor(filter.column)[0].value;
    }

    this.filters.push(filter);
  }

  removeFilter(index: number): void {
    this.filters.splice(index, 1);
  }

  operatorsFor(columnKey: string): { value: string; label: string; types: string[] }[] {
    const column = this.availableColumns.find((c) => c.key === columnKey);
    const type = column ? column.type : 'string';
    return this.operators.filter((o) => o.types.indexOf(type) !== -1);
  }

  onFilterColumnChanged(filter: ReportBuilderFilter): void {
    const operators = this.operatorsFor(filter.column);

    if (operators.every((o) => o.value !== filter.operator)) {
      filter.operator = operators.length > 0 ? operators[0].value : 'eq';
    }
  }

  needsValue(operator: string): boolean {
    return operator !== 'isnull' && operator !== 'notnull';
  }

  sortableColumns(): { key: string; label: string }[] {
    if (this.isAggregate) {
      const groupBy = this.availableColumns.filter((c) => this.selectedGroupBy[c.key]);
      const aggregates = this.aggregateRows
        .filter((a) => a.column && a.fn)
        .map((a) => ({ key: a.fn.toLowerCase() + '_' + a.column, label: a.fn + ' of ' + a.column }));

      return [...groupBy.map((c) => ({ key: c.key, label: c.label })), ...aggregates];
    }

    return this.availableColumns
      .filter((c) => this.selectedColumns[c.key])
      .map((c) => ({ key: c.key, label: c.label }));
  }

  private buildSpec(): ReportSpec {
    const spec = new ReportSpec();
    spec.entity = this.schema.key;
    spec.joins = Object.keys(this.selectedJoins).filter((k) => this.selectedJoins[k]);
    spec.filters = this.filters.filter((f) => f.column && f.operator);
    spec.sort = this.sort;
    spec.direction = this.direction;
    spec.limit = this.limit;

    if (this.isAggregate) {
      spec.groupBy = Object.keys(this.selectedGroupBy).filter((k) => this.selectedGroupBy[k]);
      spec.aggregates = this.aggregateRows.filter((a) => a.column && a.fn);
    } else {
      spec.columns = Object.keys(this.selectedColumns).filter((k) => this.selectedColumns[k]);
    }

    return spec;
  }

  runReport(): void {
    if (!this.schema) {
      return;
    }

    const spec = this.buildSpec();

    this.running = true;
    this.runFailed = false;

    this.reportBuilderService.run(spec)
      .pipe(take(1))
      .pipe(catchError((e) => {
        this.running = false;
        this.runFailed = true;
        this.runError = (e && e.error && e.error.message) ? e.error.message : 'The report could not be run.';
        return e;
      }))
      .subscribe((result: ReportResult) => {
        this.result = result;
        this.resultColumns = (result.columns || []).map((c) => ({ active: true, label: c.label, field: c.key, value: null } as GridColumn));
        this.running = false;
        this.hasRun = true;
      });
  }

  getCell(row: any, column: GridColumn): any {
    const value = row[column.field];
    return value === null || value === undefined ? '' : value;
  }

  exportToCSV(): void {
    this.reportCsvService.download(
      'report-builder-' + (this.schema ? this.schema.key : 'report'),
      this.resultColumns.map((c) => c.label),
      (this.result.rows || []).map((row) => this.resultColumns.map((c) => row[c.field])),
    );
  }

  private currentBuilderState(): AggregateBuilderState {
    return {
      joins: Object.keys(this.selectedJoins).filter((k) => this.selectedJoins[k]),
      isAggregate: this.isAggregate,
      columns: Object.keys(this.selectedColumns).filter((k) => this.selectedColumns[k]),
      groupBy: Object.keys(this.selectedGroupBy).filter((k) => this.selectedGroupBy[k]),
      aggregates: this.aggregateRows,
      filters: this.filters,
      sort: this.sort,
      direction: this.direction,
      limit: this.limit,
    };
  }

  saveReport(): void {
    if (!this.schema || !this.reportName || this.reportName.trim() === '') {
      return;
    }

    const report = this.currentSavedReport ? { ...this.currentSavedReport } as SavedReport : new SavedReport();
    report.name = this.reportName;
    report.mode = 'aggregate';
    report.entity = this.schema.key;
    report.spec = JSON.stringify(this.currentBuilderState());

    this.saving = true;
    this.saveFailed = false;

    const save$ = report.id > 0 ? this.savedReportService.update(report) : this.savedReportService.create(report);

    save$
      .pipe(take(1))
      .pipe(catchError((e) => { this.saving = false; this.saveFailed = true; return e; }))
      .subscribe((saved: SavedReport) => {
        this.saving = false;
        this.currentSavedReport = saved;
        this.selectedSavedReportId = saved.id;

        const existingIndex = this.savedReports.findIndex((r) => r.id === saved.id);
        if (existingIndex === -1) {
          this.savedReports.push(saved);
        } else {
          this.savedReports[existingIndex] = saved;
        }
      });
  }

  newReport(): void {
    this.currentSavedReport = null;
    this.selectedSavedReportId = -1;
    this.reportName = '';
  }

  loadSavedReport(id: number): void {
    const report = this.savedReports.find((r) => r.id === Number(id));

    if (!report) {
      return;
    }

    const state: AggregateBuilderState = JSON.parse(report.spec);

    this.currentSavedReport = report;
    this.selectedSavedReportId = report.id;
    this.reportName = report.name;

    this.selectedJoins = {};
    state.joins.forEach((key) => { this.selectedJoins[key] = true; });
    this.recomputeAvailableColumns();

    this.isAggregate = state.isAggregate;

    this.selectedColumns = {};
    state.columns.forEach((key) => { this.selectedColumns[key] = true; });

    this.selectedGroupBy = {};
    state.groupBy.forEach((key) => { this.selectedGroupBy[key] = true; });

    this.aggregateRows = state.aggregates || [];
    this.filters = state.filters || [];
    this.sort = state.sort || '';
    this.direction = state.direction || 'asc';
    this.limit = state.limit || 500;

    this.hasRun = false;
  }

  deleteSavedReport(): void {
    if (!this.currentSavedReport) {
      return;
    }

    this.savedReportService.delete(this.currentSavedReport.id)
      .pipe(take(1))
      .pipe(catchError((e) => e))
      .subscribe(() => {
        this.savedReports = this.savedReports.filter((r) => r.id !== this.currentSavedReport.id);
        this.newReport();
      });
  }
}
